/**
 * Module: ActiveTaskPill
 * Role: Fetches buyer's active slot (status=active → assigned/in_progress/
 *       pending_review); renders cream pill with step progress or null.
 *       A freshly claimed slot is 'assigned' (not 'in_progress'), so the
 *       aggregate 'active' filter is required to surface just-taken tasks.
 *       No bot-link fallback (DA-2, DA-7).
 *
 * Uses: @tanstack/react-query, react-router-dom, api/client, atoms/Card, atoms/Ic, tokens
 * Used by: screens/HomeScreen/index
 * Glossary: ai/glossary/buyer.md
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../api/client';
import { Card, Ic } from '../../components/atoms';
import { P, S, T } from '../../styles/tokens';

// Inline types — SSOT: src/api/v2/buyer/schemas.py SlotListItem (lines 67-78)
interface SlotListItem {
  id: string;
  offer_id: string;
  campaign_id: string;
  status: 'assigned' | 'in_progress' | 'pending_review' | 'approved' | 'rejected' | 'cancelled' | 'expired';
  current_step: number;
  reward_kopecks: number;
  deadline_at: string | null;
  created_at: string;
}
interface SlotListResponse {
  items: SlotListItem[];
  meta: { total: number; limit: number; offset: number; has_next: boolean };
}

// SYMMETRIC LIMIT — must equal max of src/api/v2/buyer/_slots_db.py:_STEP_TO_INT (done=9).
// If the backend step scale changes, update here too. (ADR-022)
const TOTAL_STEPS = 9;

export function ActiveTaskPill() {
  const navigate = useNavigate();
  const { data, isPending, isError } = useQuery<SlotListResponse>({
    queryKey: ['slots', 'active'],
    queryFn: () => apiFetch<SlotListResponse>('/api/v2/buyer/slots?status=active&limit=1'),
    retry: false,
  });

  // DA-2, DA-7: no slot → null, no external/bot fallback
  if (isPending || isError || !data || data.items.length === 0) return null;

  const slot = data.items[0];
  const pct = Math.min(100, Math.max(0, (slot.current_step / TOTAL_STEPS) * 100));

  return (
    <Card
      bg={P.cream}
      r={14}
      p={10}
      hover
      onClick={() => navigate('/flow/' + slot.id)}
      style={{ borderColor: 'rgba(212,137,14,0.30)' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Progress + label column */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginBottom: 6,
          }}>
            <p style={{
              font: T.footnote,
              fontWeight: 600,
              color: S.label,
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              Продолжи задание
            </p>
            <span style={{ font: T.caption, fontWeight: 600, color: P.warn, flexShrink: 0 }}>
              {slot.current_step}/{TOTAL_STEPS}
            </span>
          </div>
          {/* Warm amber progress bar */}
          <div style={{
            height: 3,
            borderRadius: 2,
            background: 'rgba(212,137,14,0.18)',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${pct}%`,
              background: P.warn,
              borderRadius: 2,
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>
        {/* Trailing chevron */}
        <Ic name="chevR" size={14} color={P.warn} style={{ flexShrink: 0 }} />
      </div>
    </Card>
  );
}
